import {
  GenealogyEvent,
  EventSourceCitation,
  EventType,
  Note,
  Person,
  Relationship,
} from '../../types.ts';
import { isPrivacyRedactedRaw } from '../../utils/privacy.ts';
import { naarGedcomLeeftijd } from './gedcomAge';
import {
  EVENT_TO_GEDCOM_TAG,
  EVEN_TYPE_DESCRIPTOR,
  gedcomExportTag,
  isBilateralEvent,
  GEDCOM_ATTRIBUTE_TAGS_WITH_VALUE,
  GEDCOM_EVENT_TAGS_ALLOWING_Y,
  PARTICIPANT_TO_GEDCOM_ROLE,
  FACT_KEY_TO_GEDCOM_ATTRIBUTE_TAG,
} from '../EventUtils.ts';
import { buildGedcomNameBlock } from './gedcomNameUtils.ts';
import { peopleApi } from './people.ts';
import { relationshipsApi } from './relationships.ts';
import { storiesApi } from './stories.ts';
import { sourcesApi } from './sources.ts';
import { eventsApi } from './events.ts';
import { mediaApi } from './media.ts';
import { notesApi } from './notes.ts';
import { placesApi } from './places.ts';
import { projectsApi } from './projects.ts';
import { sosaPerPersoon } from '../../utils/sosaPerPersoon.ts';
import { archivesApi } from './archives.ts';
import {
  buildSourceLines,
  SOURCE_TYPE_TO_GEDCOM_EVENT,
  type CitationLog,
} from './gedcomCitation.ts';
import { subtypeToGedcomPedi } from './gedcomPedi.ts';
import { isParentRel, effectiveChildSubtype } from '../../utils/pediType.ts';
import { partnerSubtypeToGedcomTag } from './gedcomPartner.ts';
import type { Repository } from '../../types.ts';
import { isNameType, FALLBACK_NAME_TYPE } from '../../utils/nameTypes.ts';

// Bug E fix: convert ISO dates (YYYY-MM-DD) to GEDCOM date format (DD MON YYYY)
const GEDCOM_MONTHS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];
function toGedcomDate(dateStr: string): string {
  if (!dateStr) return '';
  // Already in GEDCOM format (contains a month abbreviation)
  if (/[A-Z]{3}/.test(dateStr)) return dateStr;
  // ISO YYYY-MM-DD
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    return `${parseInt(d)} ${GEDCOM_MONTHS[parseInt(m) - 1]} ${y}`;
  }
  // ISO YYYY-MM
  const isoMonthMatch = dateStr.match(/^(\d{4})-(\d{2})$/);
  if (isoMonthMatch) {
    const [, y, m] = isoMonthMatch;
    return `${GEDCOM_MONTHS[parseInt(m) - 1]} ${y}`;
  }
  // Pass through anything else (already formatted or just a year)
  return dateStr;
}

export const gedcomApi = {
  /**
   * @param opts.excludeLiving D-15/B-52 — laat beschermde personen (levend, of
   *   minder dan 100 jaar geleden geboren zonder overlijdensdatum) volledig uit
   *   het bestand. Voor een genealoog die zijn werk aan een klant of website
   *   levert is dat het verschil tussen wel en niet mogen publiceren. Zonder deze
   *   optie kon dat alleen door ze eerst handmatig uit het project te halen.
   */
  exportGedcom: async (
    projectId: string,
    /**
     * #1773 — `stepKey` is een i18n-sleutel (met `params` voor de teller); de
     * aanroeper vertaalt. Zo blijft deze functie taalloos en de GEDCOM-uitvoer
     * ongemoeid.
     */
    onProgress?: (stepKey: string, pct: number, params?: Record<string, string>) => void,
    gedcomVersion: string = '5.5.1',
    /**
     * #1586 — `redactYears`: de privacytermijn van het project
     * (settings.privacy.redactYears); zonder: 100 jaar, zoals voorheen.
     */
    opts: { excludeLiving?: boolean; redactYears?: number; probandId?: string | null } = {}
  ): Promise<Blob> => {
    const progress = onProgress || (() => {});
    const is70 = gedcomVersion === '7.0';

    // SECURITY: GEDCOM export uses authenticated API endpoints (getPeople, etc.)
    // which apply applyPrivacyFilter server-side. No direct DB bypass possible.
    // ── Step 1: Fetch all data ──
    progress('hsGedExportFetching', 5);
    const [
      allPeopleRaw,
      allRelsRaw,
      stories,
      sources,
      allEventsRaw,
      mediaList,
      projectNotes,
      places,
      repositories,
      archiveImportLog,
    ] = await Promise.all([
      // D-03 — notes + biography horen in de export; die zitten niet in de standaardlijst.
      peopleApi.getPeople(projectId, { includeNotes: true }),
      relationshipsApi.getRelationships(projectId),
      storiesApi.getStories(projectId),
      sourcesApi.getSources(projectId),
      eventsApi.getEvents(projectId).catch(() => [] as GenealogyEvent[]),
      mediaApi.getMediaList(projectId).catch(() => [] as any[]),
      notesApi.getProjectNotes(projectId).catch(() => [] as Note[]),
      placesApi.getPlaces(projectId).catch(() => [] as any[]),
      projectsApi.getRepositories(projectId).catch(() => [] as Repository[]),
      // #856 — citation-data per source voor GEDCOM SOUR-uitbreiding.
      // Graceful fallback bij geen tier-access of niet-pro projecten:
      // archivesApi.archiveGetImportLogProject retourneert { imports: [] }.
      archivesApi.archiveGetImportLogProject(projectId).catch(() => ({ imports: [] })),
    ]);

    // Bouw lookup van source_id → archive_import_log row (laatste import wint
    // bij meerdere imports per source — projects.archive/import-log sorteert
    // ORDER BY imported_at DESC, dus eerste rij is de meest recente).
    const citationBySourceId = new Map<string, CitationLog>();
    if (archiveImportLog && Array.isArray(archiveImportLog.imports)) {
      for (const row of archiveImportLog.imports) {
        const r = row as Record<string, unknown>;
        const sourceId = r.source_id as string | null;
        if (sourceId && !citationBySourceId.has(sourceId)) {
          citationBySourceId.set(sourceId, r as unknown as CitationLog);
        }
      }
    }
    // #1710 — Sosa berekend op de VOLLEDIGE stamboom (vóór de privacyfilter), zoals
    // de stamboom het toont; niet de opgeslagen kolom, die liep achter. Zonder
    // proband geen _SOSA, liever dan een verouderd nummer.
    const sosaMap = sosaPerPersoon(opts.probandId, allPeopleRaw, allRelsRaw);
    let people = allPeopleRaw;
    let rels = allRelsRaw;
    let allEvents = allEventsRaw;

    // D-15 — beschermde personen eruit filteren VOOR er iets wordt opgebouwd.
    // Ook hun relaties en gebeurtenissen moeten weg: een FAM of EVEN die naar een
    // niet-geexporteerde INDI wijst maakt het bestand kapot, en andere programma's
    // melden dat niet netjes maar slikken de verwijzing stil.
    if (opts.excludeLiving) {
      const excluded = new Set(
        people
          .filter((p: Person) => isPrivacyRedactedRaw(p, opts.redactYears ?? 100))
          .map((p: Person) => p.id)
      );
      if (excluded.size > 0) {
        people = people.filter((p: Person) => !excluded.has(p.id));
        rels = rels.filter(
          (r: Relationship) => !excluded.has(r.personA) && !excluded.has(r.personB)
        );
        allEvents = allEvents.filter(
          (e: GenealogyEvent) =>
            !(e.participants || []).some((pt) => pt.personId && excluded.has(pt.personId))
        );
      }
    }

    progress('hsGedExportFetched', 20);

    // Place coordinate lookup (Refs #668 — LATI/LONG in PLAC)
    const placeMap = new Map<string, any>();
    for (const place of places) {
      if (place.id) placeMap.set(place.id, place);
    }

    // Repository xref map (Refs #668 — REPO records + SOUR→REPO link)
    const repoIdMap = new Map<string, string>();
    repositories.forEach((r: Repository, i: number) => repoIdMap.set(r.id, `@R${i + 1}@`));

    // Helper: write PLAC with optional MAP/LATI/LONG sub-tags.
    // placesApi transformeert server (latitude/longitude) naar client (lat/lng) — we checken beide.
    const writePlace = (level: number, placeName: string, placeId?: string): string => {
      let out = `${level} PLAC ${placeName}\n`;
      const place = placeId ? placeMap.get(placeId) : undefined;
      const lat = place?.lat ?? place?.latitude;
      const lon = place?.lng ?? place?.longitude;
      if (lat != null && lon != null) {
        const latN = Number(lat);
        const lonN = Number(lon);
        const latStr = (latN >= 0 ? 'N' : 'S') + Math.abs(latN).toFixed(4);
        const lonStr = (lonN >= 0 ? 'E' : 'W') + Math.abs(lonN).toFixed(4);
        out += `${level + 1} MAP\n`;
        out += `${level + 2} LATI ${latStr}\n`;
        out += `${level + 2} LONG ${lonStr}\n`;
      }
      return out;
    };

    // ── HTML stripper: GEDCOM is plain text ──
    const stripHtml = (text: string): string => {
      if (!text) return '';
      return text
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>\s*<p>/gi, '\n\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();
    };

    // ── CONC/CONT helper: split lines > 248 chars per GEDCOM 5.5.1 spec ──
    const MAX_LINE_LEN = 248;
    const gedLine = (level: number, tag: string, value: string): string => {
      const firstLine = `${level} ${tag} `;
      const maxFirst = MAX_LINE_LEN - firstLine.length;
      if (value.length <= maxFirst && !value.includes('\n')) {
        return `${level} ${tag} ${value}\n`;
      }
      // Split on newlines first (CONT), then on length (CONC)
      let result = '';
      const lines = value.split('\n');
      for (let li = 0; li < lines.length; li++) {
        let text = lines[li];
        const isFirst = li === 0 && result === '';
        if (isFirst) {
          const chunk = text.substring(0, maxFirst);
          result += `${level} ${tag} ${chunk}\n`;
          text = text.substring(maxFirst);
        } else {
          // CONT for newline continuation
          const maxCont = MAX_LINE_LEN - `${level + 1} CONT `.length;
          const chunk = text.substring(0, maxCont);
          result += `${level + 1} CONT ${chunk}\n`;
          text = text.substring(maxCont);
        }
        // CONC for remaining overflow
        while (text.length > 0) {
          const maxConc = MAX_LINE_LEN - `${level + 1} CONC `.length;
          const chunk = text.substring(0, maxConc);
          result += `${level + 1} CONC ${chunk}\n`;
          text = text.substring(maxConc);
        }
      }
      return result;
    };

    // ── Build maps ──
    const biographyMap = new Map<string, string>();
    for (const story of stories) {
      if (story.type === 'biography' && story.content) {
        for (const pid of story.taggedPersonIds) {
          const plainText = story.content.replace(/<[^>]+>/g, '').trim();
          if (plainText) biographyMap.set(pid, plainText);
        }
      }
    }

    // Person ID → xref
    const personIdMap = new Map<string, string>();
    people.forEach((p: any, i: number) => personIdMap.set(p.id, `@I${i + 1}@`));

    // Source ID → xref
    const sourceIdMap = new Map<string, string>();
    sources.forEach((s: any, i: number) => sourceIdMap.set(s.id, `@S${i + 1}@`));

    // Media ID → xref
    const mediaIdMap = new Map<string, string>();
    mediaList.forEach((m: any, i: number) => mediaIdMap.set(m.id, `@O${i + 1}@`));

    // Note ID → xref
    const noteIdMap = new Map<string, string>();
    projectNotes.forEach((n: any, i: number) => noteIdMap.set(n.id, `@N${i + 1}@`));

    // GEDCOM 5.5.1 requires events grouped into INDI (person) and FAM (relationship) records.
    // Group events by participants: subject → person event, partner pair → relationship event.
    const personEventsMap = new Map<string, GenealogyEvent[]>();
    const relEventsMap = new Map<string, GenealogyEvent[]>();
    // #1389 B11 — huwelijk met onbekende partner: geen relatie om aan te hangen,
    // en onder INDI mag MARR niet staan. Zo'n event krijgt een FAM met één partner.
    const loneFamEvents = new Map<string, GenealogyEvent[]>();

    // Build a lookup: "personA|personB" → relationshipId (sorted to ensure consistent key)
    const partnerPairToRelId = new Map<string, string>();
    for (const rel of rels) {
      if (rel.personA && rel.personB) {
        const key = [rel.personA, rel.personB].sort().join('|');
        partnerPairToRelId.set(key, rel.id);
      }
    }

    for (const ev of allEvents) {
      if (!ev.participants || ev.participants.length === 0) continue;

      // 'primary' is een geaccepteerd legacy-synoniem voor 'subject' (zie
      // EventRow.tsx:100-101, migration 070) — de rollen-picker in EventModal.tsx
      // toont alleen 'primary' als hoofdpersoon-optie. Zonder deze OR viel elk
      // handmatig toegevoegd event zonder 'subject'-participant stil uit de export
      // (NC #642, "nalatenschap"-event verdween uit de GEDCOM).
      const subjects = ev.participants.filter(
        (p: any) => p.role === 'subject' || p.role === 'primary'
      );
      const partners = ev.participants.filter((p: any) => p.role === 'partner');

      if (isBilateralEvent(ev.type) && partners.length < 2) {
        const pid = (partners[0] ?? subjects[0])?.personId;
        if (pid) {
          if (!loneFamEvents.has(pid)) loneFamEvents.set(pid, []);
          loneFamEvents.get(pid)!.push(ev);
        }
        continue;
      }

      if (subjects.length > 0) {
        // Person event: group by subject's personId
        for (const s of subjects) {
          if (!s.personId) continue;
          if (!personEventsMap.has(s.personId)) personEventsMap.set(s.personId, []);
          personEventsMap.get(s.personId)!.push(ev);
        }
      } else if (partners.length >= 2) {
        // Relationship event: match partner pair to a relationship
        const partnerIds = partners.map((p: any) => p.personId).filter(Boolean);
        if (partnerIds.length >= 2) {
          const key = [partnerIds[0], partnerIds[1]].sort().join('|');
          const relId = partnerPairToRelId.get(key);
          if (relId) {
            if (!relEventsMap.has(relId)) relEventsMap.set(relId, []);
            relEventsMap.get(relId)!.push(ev);
          }
        }
      } else if (partners.length === 1) {
        // Single partner — treat as person event for that partner (edge case)
        const pid = partners[0].personId;
        if (pid) {
          if (!personEventsMap.has(pid)) personEventsMap.set(pid, []);
          personEventsMap.get(pid)!.push(ev);
        }
      }
    }

    // Helper to write SOUR references (JSONB backward compat)
    // #1318 — `pages` is optioneel: de vindplaats binnen de bron (folio,
    // aktenummer), per bron-id. GEDCOM zet die op de citatie en niet op het
    // bronrecord, want dezelfde bron wordt door tientallen personen gedeeld
    // terwijl de vindplaats per persoon verschilt. Ontbreekt hij, dan houdt de
    // ontvanger "DTB Zutphen" over zonder te weten wáár in dat boek.
    const writeSourRefs = (
      level: number,
      ids: string[] | undefined,
      pages?: Record<string, string>
    ): string => {
      if (!ids || !ids.length) return '';
      return ids
        .map((sid) => {
          const x = sourceIdMap.get(sid);
          if (!x) return '';
          let out = `${level} SOUR ${x}
`;
          const page = pages?.[sid];
          if (page) out += gedLine(level + 1, 'PAGE', page);
          return out;
        })
        .join('');
    };

    // Enhanced source citation export with PAGE and QUAY (for events table)
    const writeSourCitations = (
      level: number,
      eventSources: EventSourceCitation[] | undefined
    ): string => {
      if (!eventSources || !eventSources.length) return '';
      return eventSources
        .map((es: EventSourceCitation) => {
          const xref = sourceIdMap.get(es.sourceId);
          if (!xref) return '';
          let out = `${level} SOUR ${xref}\n`;
          if (es.page) out += `${level + 1} PAGE ${es.page}\n`;
          if (es.quality != null && es.quality >= 0 && es.quality <= 3)
            out += `${level + 1} QUAY ${es.quality}\n`;
          if (es.notes) out += gedLine(level + 1, 'NOTE', es.notes);
          return out;
        })
        .join('');
    };

    // ── GEDCOM Header ──
    const now = new Date();
    const MONTHS = [
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ];
    const exportDate = `${now.getDate()} ${MONTHS[now.getMonth()]} ${now.getFullYear()}`;
    const exportTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

    // HEAD block: PLAC.FORM hierarchie meegeven zodat importers weten hoe plaatsen
    // gestructureerd zijn (Plaats, Gemeente, Provincie, Land). Refs #668.
    const placHierarchy = `1 PLAC\n2 FORM Plaats, Gemeente, Provincie, Land\n`;
    let ged = is70
      ? `0 HEAD\n1 GEDC\n2 VERS 7.0\n1 SOUR Pecktree\n2 VERS 2.0\n1 SCHMA\n2 TAG _PECKTREE https://pecktree.nl/gedcom/ext\n${placHierarchy}`
      : `0 HEAD\n1 SOUR Pecktree\n2 NAME Pecktree\n2 VERS 2.0\n1 DATE ${exportDate}\n2 TIME ${exportTime}\n1 CHAR UTF-8\n1 GEDC\n2 VERS 5.5.1\n2 FORM LINEAGE-LINKED\n${placHierarchy}`;

    // ── Pre-compute FAM xrefs for FAMS/FAMC backpointers ──
    // Bug C fix: use correct DB type strings for spousal relationships
    const famTypes = ['spouse', 'spousal'];
    const processedFamilies = new Set<string>();
    let famIndex = 0;
    const famIdMap = new Map<string, string>();
    // personId → list of FAM xrefs where person is spouse (FAMS)
    const personFamsMap = new Map<string, string[]>();
    // personId → list of FAM xrefs where person is child (FAMC)
    const personFamcMap = new Map<string, string[]>();
    // Store FAM data for later writing
    interface FamRecord {
      xref: string;
      rel: any;
      husbXref: string;
      wifeXref: string;
      children: string[];
      childIds: string[];
    }
    const famRecords: FamRecord[] = [];

    // Build spousal FAM records
    rels
      .filter((r: any) => famTypes.includes(r.type))
      .forEach((rel: any) => {
        const famKey = [rel.personA, rel.personB].sort().join('_') + '_' + rel.type;
        if (processedFamilies.has(famKey)) return;
        processedFamilies.add(famKey);
        famIndex++;
        const famXref = `@F${famIndex}@`;
        famIdMap.set(famKey, famXref);

        // Gender-aware HUSB/WIFE assignment
        const personA = people.find((p: any) => p.id === rel.personA);
        const personB = people.find((p: any) => p.id === rel.personB);
        let husbId = rel.personA,
          wifeId = rel.personB;
        if (personA?.gender === 'F' && personB?.gender === 'M') {
          husbId = rel.personB;
          wifeId = rel.personA;
        }
        const husbXref = personIdMap.get(husbId) || `@I${husbId}@`;
        const wifeXref = personIdMap.get(wifeId) || `@I${wifeId}@`;

        // Track FAMS for both spouses
        const addFams = (pid: string) => {
          const arr = personFamsMap.get(pid) || [];
          arr.push(famXref);
          personFamsMap.set(pid, arr);
        };
        addFams(rel.personA);
        addFams(rel.personB);

        // Collect children — only include if child has a parent rel with BOTH spouses in this FAM
        // This prevents children from being assigned to a step-parent's FAM record
        const exportedChildren = new Set<string>();
        const childXrefs: string[] = [];
        // Get all children of personA and personB separately
        const childrenOfA = new Set(
          rels
            .filter((r: any) => isParentRel(r) && r.personA === rel.personA)
            .map((r: any) => r.personB)
        );
        const childrenOfB = new Set(
          rels
            .filter((r: any) => isParentRel(r) && r.personA === rel.personB)
            .map((r: any) => r.personB)
        );
        // Only children that appear in BOTH sets belong to this family
        for (const childId of childrenOfA) {
          if (childrenOfB.has(childId) && !exportedChildren.has(childId)) {
            exportedChildren.add(childId);
            const childXref = personIdMap.get(childId) || `@I${childId}@`;
            childXrefs.push(childXref);
            // Track FAMC for child
            const arr = personFamcMap.get(childId) || [];
            arr.push(famXref);
            personFamcMap.set(childId, arr);
          }
        }

        famRecords.push({
          xref: famXref,
          rel,
          husbXref,
          wifeXref,
          children: childXrefs,
          childIds: [...exportedChildren],
        });
      });

    // #1389 B11 — FAM-records met één partner (zie loneFamEvents hierboven)
    const loneFams: { xref: string; personId: string; events: GenealogyEvent[] }[] = [];
    for (const [pid, evs] of loneFamEvents) {
      famIndex++;
      const xref = `@F${famIndex}@`;
      loneFams.push({ xref, personId: pid, events: evs });
      const arr = personFamsMap.get(pid) || [];
      arr.push(xref);
      personFamsMap.set(pid, arr);
    }

    // Build FAM records for children the spousal pass above did not cover.
    // Group by the child's PARENT SET, not per parent: two parents without a
    // registered partner-relationship still form ONE family. A FAM is valid
    // without a MARR, whereas a FAM per parent gives the child two FAMC lines —
    // importers (Geneanet) keep only the first and silently drop the other
    // parent along with that parent's entire ancestry.
    // Parents are grouped per exported PEDI tag, because PEDI hangs on the
    // child's FAMC and therefore cannot differ within a single FAM.
    const allExportedChildren = new Set<string>();
    for (const fam of famRecords) {
      for (const cid of fam.childIds) allExportedChildren.add(cid);
    }
    // childId → pedi group → parentIds
    const childParents = new Map<string, Map<string, string[]>>();
    rels
      .filter((r: any) => isParentRel(r) && !allExportedChildren.has(r.personB))
      .forEach((r: any) => {
        const group = subtypeToGedcomPedi(effectiveChildSubtype(r)) || 'bio';
        const byGroup = childParents.get(r.personB) || new Map<string, string[]>();
        const parents = byGroup.get(group) || [];
        if (!parents.includes(r.personA)) parents.push(r.personA);
        byGroup.set(group, parents);
        childParents.set(r.personB, byGroup);
      });
    // parent set + pedi group → the children sharing that exact set (siblings)
    const famGroups = new Map<string, { parentIds: string[]; childIds: string[] }>();
    for (const [childId, byGroup] of childParents) {
      for (const [group, parents] of byGroup) {
        // ponytail: >2 parents in one PEDI class is a data anomaly — keep the old
        // FAM-per-parent shape rather than drop a parent to fit HUSB/WIFE.
        const sets = parents.length <= 2 ? [parents] : parents.map((p) => [p]);
        for (const set of sets) {
          const key = `${group}|${[...set].sort().join(',')}`;
          const entry = famGroups.get(key) || { parentIds: set, childIds: [] };
          entry.childIds.push(childId);
          famGroups.set(key, entry);
        }
      }
    }
    const parentFams: { xref: string; parentIds: string[]; childIds: string[] }[] = [];
    for (const g of famGroups.values()) {
      famIndex++;
      const famXref = `@F${famIndex}@`;
      // Track FAMS for the parents
      for (const parentId of g.parentIds) {
        const arr = personFamsMap.get(parentId) || [];
        arr.push(famXref);
        personFamsMap.set(parentId, arr);
      }
      // Track FAMC for the children
      for (const childId of g.childIds) {
        const cArr = personFamcMap.get(childId) || [];
        cArr.push(famXref);
        personFamcMap.set(childId, cArr);
      }
      parentFams.push({ xref: famXref, parentIds: g.parentIds, childIds: g.childIds });
    }

    // ── #1089: Build PEDI lookup map (child × FAM → GEDCOM PEDI string) ──
    // For each parent-child relationship, find which FAM record the child belongs to via
    // that parent, and if the relationship has a relevant subtype write the PEDI tag.
    // Map: childId → Map<famXref, pediTag>
    const childPediMap = new Map<string, Map<string, string>>();
    for (const rel of rels) {
      if (!isParentRel(rel)) continue;
      const pediTag = subtypeToGedcomPedi(effectiveChildSubtype(rel));
      if (!pediTag) continue; // biological / step / suspected / acknowledged → no PEDI tag
      const parentId = rel.personA;
      const childId = rel.personB;
      // Find which FAM records include this parent AND this child
      for (const fam of famRecords) {
        if (
          fam.childIds.includes(childId) &&
          (fam.rel.personA === parentId || fam.rel.personB === parentId)
        ) {
          const inner = childPediMap.get(childId) || new Map<string, string>();
          // Only set once per famXref (first non-null pediTag wins)
          if (!inner.has(fam.xref)) inner.set(fam.xref, pediTag);
          childPediMap.set(childId, inner);
        }
      }
      for (const spf of parentFams) {
        if (spf.childIds.includes(childId) && spf.parentIds.includes(parentId)) {
          const inner = childPediMap.get(childId) || new Map<string, string>();
          if (!inner.has(spf.xref)) inner.set(spf.xref, pediTag);
          childPediMap.set(childId, inner);
        }
      }
    }

    // ── Step 2: Generate INDI records ──
    progress('hsGedExportPeople', 30);
    people.forEach((p: any, idx: number) => {
      const xref = personIdMap.get(p.id) || `@I${p.id}@`;
      const sex = p.gender === 'M' ? 'M' : p.gender === 'F' ? 'F' : 'U';
      ged += `0 ${xref} INDI\n`;
      // Refs #781 — NAME-block met patronym in NAME-string + 2 _PATR voor round-trip.
      ged += buildGedcomNameBlock({
        givenNames: p.givenNames,
        patronym: p.patronym,
        surname: p.surname,
        surnamePrefix: p.surnamePrefix,
        prefix: p.prefix,
        suffix: p.suffix,
        nickname: p.nickname,
      });
      ged += `1 SEX ${sex}\n`;

      // Alternative names with source references
      const altNames = p.alternativeNames || [];
      for (const alias of altNames) {
        if (alias.givenNames || alias.surname) {
          const altSpfx = alias.surnamePrefix ? alias.surnamePrefix + ' ' : '';
          // Patroniem in de NAME-string, net als bij de hoofdnaam (#781).
          const altVoor = [alias.givenNames, alias.patronym].filter(Boolean).join(' ');
          ged += `1 NAME ${altVoor} /${altSpfx}${alias.surname || ''}/\n`;
          // NC #827 — onze types zijn de GEDCOM-waarden; onbekend → 'aka'.
          ged += `2 TYPE ${isNameType(alias.type) ? alias.type : FALLBACK_NAME_TYPE}\n`;
          if (alias.surnamePrefix) ged += `2 SPFX ${alias.surnamePrefix}\n`;
          if (alias.surname) ged += `2 SURN ${alias.surname}\n`;
          // `_PATR` is onze eigen tag en de betrouwbare kant van de round-trip:
          // uit de NAME-string is een patroniem niet van een tweede voornaam te
          // onderscheiden (#781).
          if (alias.patronym) ged += `2 _PATR ${alias.patronym}\n`;
          ged += writeSourRefs(2, alias.sourceIds);
        }
      }

      if (p.title) ged += `1 TITL ${p.title}\n`;
      if (p.refNumber) ged += `1 REFN ${p.refNumber}\n`;
      if (p.restriction) ged += `1 RESN ${p.restriction}\n`;
      // Stable identifier across roundtrips — 7.0 gebruikt UID, 5.5.1 _UID als custom tag
      // (_UID wordt door Aldfaer, RootsMagic, Legacy herkend voor dedupe). Refs #668.
      if (is70) {
        if (p.externalId) ged += `1 EXID ${p.externalId}\n`;
        ged += `1 UID ${p.uid || p.id}\n`;
      } else {
        ged += `1 _UID ${p.uid || p.id}\n`;
      }

      // Events from events table (needed early for vital event enrichment)
      const eventsToExport = personEventsMap.get(p.id) || [];

      // Helper: find matching event from events table and write NOTE + SOUR sub-tags
      const writeEventExtras = (evType: string) => {
        const matchingEv = eventsToExport.find((ev: any) => ev.type === evType);
        if (!matchingEv) return '';
        let extra = '';
        if (matchingEv.description) extra += gedLine(2, 'NOTE', stripHtml(matchingEv.description));
        if (matchingEv.sources) extra += writeSourCitations(2, matchingEv.sources);
        else if (matchingEv.sourceIds?.length) extra += writeSourRefs(2, matchingEv.sourceIds);
        return extra;
      };

      /**
       * Vitaal blok (#1291).
       *
       * De kolommen op `people` zijn leidend, maar als die leeg zijn terwijl er
       * wél een gebeurtenis van dat type ligt, wint de gebeurtenis. Zonder die
       * terugval viel zo'n gebeurtenis volledig weg: dit blok hing alléén aan de
       * kolommen, en de gebeurtenissen-lus verderop slaat de vier vitale types
       * juist over. Gemeten op de dev-database: 67 gebeurtenissen (65 overlijden,
       * 1 geboorte, 1 doop) — inclusief hun bron-koppelingen, want die reizen
       * met het blok mee.
       *
       * Draagt het blok geen datum en geen plaats, dan krijgt het de waarde `Y`.
       * GEDCOM 5.5.1 schrijft `n <<EVENT_TYPE>> [Y|<NULL>]` voor: `Y` betekent
       * "het gebeurde, meer weten we niet". Een kale tag zonder detail én zonder
       * `Y` is betekenisloos en mag door een lezer worden overgeslagen.
       */
      const writeVitaalBlok = (
        tag: string,
        evType: string,
        kolomDatum?: string,
        kolomPlaats?: string,
        kolomPlaatsId?: string,
        naDePlaats?: string
      ): string => {
        const ev = eventsToExport.find((e: { type?: string }) => e.type === evType) as
          { date?: string; placeName?: string; placeId?: string } | undefined;
        const datum = kolomDatum || ev?.date;
        const plaats = kolomPlaats || ev?.placeName;
        if (!datum && !plaats && !ev) return '';

        const extras = (naDePlaats || '') + writeEventExtras(evType);
        // Geen datum én geen plaats: alleen dan draagt de tag de waarde `Y`.
        // Staat er verder wél iets onder (een NOTE of een bron), dan is de tag
        // niet leeg en hoort `Y` er juist NIET te staan.
        if (!datum && !plaats) return extras ? `1 ${tag}\n${extras}` : `1 ${tag} Y\n`;

        let out = `1 ${tag}\n`;
        // Bug E fix: convert ISO date to GEDCOM format
        if (datum) out += `2 DATE ${toGedcomDate(datum)}\n`;
        if (plaats) out += writePlace(2, plaats, kolomPlaatsId || ev?.placeId);
        return out + extras;
      };

      // Vital events — with notes and sources from events table
      ged += writeVitaalBlok('BIRT', 'birth', p.birthDate, p.birthPlace, p.birthPlaceId);
      const deatBlok = writeVitaalBlok(
        'DEAT',
        'death',
        p.deathDate,
        p.deathPlace,
        p.deathPlaceId,
        p.causeOfDeath ? `2 CAUS ${p.causeOfDeath}\n` : ''
      );
      // Een overleden persoon zonder énige overlijdensgegevens krijgt nog steeds
      // `1 DEAT Y` — dat feit is op zichzelf informatie voor de ontvanger.
      if (deatBlok) ged += deatBlok;
      else if (!p.isLiving) ged += `1 DEAT Y\n`;
      ged += writeVitaalBlok('BAPM', 'baptism', p.baptismDate, p.baptismPlace, p.baptismPlaceId);
      ged += writeVitaalBlok('BURI', 'burial', p.burialDate, p.burialPlace, p.burialPlaceId);

      // #616: occupation wordt uitsluitend geëxporteerd via de OCCU-events
      // hieronder (loop door eventsToExport). Geen fallback meer op p.occupation.

      // D-05 — RELI stond er dubbel in.
      //
      // `p.religion` (de kolom) werd hier als `1 RELI` weggeschreven, en een
      // religie-GEBEURTENIS schreef verderop in de events-lus nóg een `1 RELI`. Een
      // roundtrip verdubbelde de religie dus bij elke ronde. Alleen de kolom
      // uitgeven als er geen religie-gebeurtenis is die het al doet.
      const heeftReligieEvent = eventsToExport.some(
        (ev: { type?: string }) => ev.type === 'religion'
      );
      if (p.religion && !heeftReligieEvent) ged += gedLine(1, 'RELI', p.religion);

      // Bug 13 fix: merge notes and biography into a single NOTE to avoid duplicate NOTE tags
      const bioNote =
        biographyMap.get(p.id) || (p.biography ? p.biography.replace(/<[^>]+>/g, '').trim() : '');
      const combinedNote = [p.notes, bioNote]
        .filter(Boolean)
        .map((n) => stripHtml(n))
        .join('\n\n---\n\n');
      if (combinedNote) ged += gedLine(1, 'NOTE', combinedNote);

      // Person→Source references (only those not already exported with events)
      // Bug G fix: collect source IDs from ALL event types, not just vital events
      const eventSourceIds = new Set<string>();
      for (const ev of eventsToExport) {
        // #1291 — deze verzameling zegt "die bron staat al bij een gebeurtenis,
        // dus niet nóg eens op de persoon". Dat mag alleen gelden voor
        // gebeurtenissen die ook echt worden weggeschreven. Een negatieve
        // bewering gaat als `1 NO <TAG>` de export in en draagt géén SOUR, dus
        // die bron is nergens geplaatst en hoort bij de persoon te blijven
        // staan — anders verdwijnt hij uit het bestand.
        if (is70 && ev.isNegated) continue;
        for (const sid of ev.sourceIds || []) eventSourceIds.add(sid);
        for (const s of ev.sources || []) eventSourceIds.add(s.sourceId);
      }
      const remainingSourceIds = (p.sourceIds || []).filter(
        (sid: string) => !eventSourceIds.has(sid)
      );
      ged += writeSourRefs(1, remainingSourceIds, p.sourcePages);

      // #619 Fase C: custom_facts export
      // Mapped attribute keys (description/education/caste/...) krijgen hun
      // native GEDCOM attribute tag (DSCR, EDUC, CAST, ...). Onbekende/custom
      // keys worden als generieke FACT-records geëxporteerd met TYPE subtag.
      // Bron-koppelingen per fact komen via 2 SOUR.
      if (p.facts && typeof p.facts === 'object' && !Array.isArray(p.facts)) {
        for (const [factKey, factEntryRaw] of Object.entries(p.facts as Record<string, unknown>)) {
          const factEntry = factEntryRaw as {
            value?: string;
            sourceIds?: string[];
            date?: string;
            place?: string;
          } | null;
          if (!factEntry || typeof factEntry !== 'object') continue;
          const value = (factEntry.value || '').trim();
          const factDate = (factEntry.date || '').trim();
          const factPlace = (factEntry.place || '').trim();
          // #1253 — een attribuut zonder waarde maar mét datum of plaats is
          // geldige GEDCOM (`1 EDUC` / `2 DATE 1865`). Dat sloegen we hier over,
          // waardoor zo'n regel spoorloos verdween over een import-export-cyclus.
          if (!value && !factDate && !factPlace) continue;
          // Skip 'title' fact als de legacy p.title kolom al iets bevat — dat
          // pad schrijft al een `1 TITL` record op regel 427. Voorkomt duplicates
          // voor pre-#619 data die nog in de title-kolom staat.
          if (factKey === 'title' && p.title) continue;
          const attributeTag = FACT_KEY_TO_GEDCOM_ATTRIBUTE_TAG[factKey];
          if (attributeTag) {
            // Bekende attribute tag: `1 DSCR <val>`, `1 EDUC <val>`, etc.
            ged += gedLine(1, attributeTag, stripHtml(value));
          } else {
            // Custom fact: `1 FACT <val>` / `2 TYPE <key>`
            ged += gedLine(1, 'FACT', stripHtml(value));
            ged += `2 TYPE ${factKey}\n`;
          }
          // EVENT_DETAIL onder het attribuut (#1253), in GEDCOM-volgorde:
          // DATE vóór PLAC.
          if (factDate) ged += `2 DATE ${factDate}\n`;
          if (factPlace) ged += gedLine(2, 'PLAC', factPlace);
          ged += writeSourRefs(2, factEntry.sourceIds);
        }
      }

      // Custom events (from events table)
      for (const ev of eventsToExport) {
        const evType = ev.type;
        if (['birth', 'death', 'baptism', 'burial'].includes(evType)) continue;
        if (['marriage', 'divorce'].includes(evType)) continue;

        // Determine GEDCOM tag
        const gedTag = gedcomExportTag(ev.type, ev.gedcomTag) || 'EVEN';

        // GEDCOM 7.0: negative assertions (NO tag)
        if (is70 && ev.isNegated) {
          ged += `1 NO ${gedTag}\n`;
          // Bug E fix: convert ISO date to GEDCOM format
          if (ev.date) ged += `2 DATE ${toGedcomDate(ev.date)}\n`;
          continue;
        }

        // Write GEDCOM tag line
        if (gedTag === 'OCCU') {
          // Bug A fix: fall back to ev.description when ev.title is empty
          const occVal = (ev.title || ev.description || '').replace(
            /^(Occupation|Beroep):\s*/i,
            ''
          );
          ged += `1 OCCU ${occVal}\n`;
        } else if (gedTag === 'EVEN') {
          // NC #636 — de titel is de WAARDE, `2 TYPE` classificeert de soort.
          // Andersom (titel in TYPE) maakte een militaire dienst met de titel
          // "Reserve" onherkenbaar als militaire dienst voor lezers zoals
          // Ancestral Author. Valt de soort niet onder de vier bekende
          // descriptors, dan blijft de titel als TYPE staan — beter een ruwe
          // classificatie dan geen.
          const descriptor = EVEN_TYPE_DESCRIPTOR[ev.type as EventType];
          if (descriptor) {
            if (ev.title) ged += gedLine(1, 'EVEN', ev.title);
            else ged += `1 EVEN\n`;
            ged += `2 TYPE ${descriptor}\n`;
          } else {
            ged += `1 EVEN\n`;
            if (ev.title) ged += `2 TYPE ${ev.title}\n`;
          }
        } else {
          // NC #635 — de titel ging bij élke getagde gebeurtenis verloren: alleen
          // OCCU en EVEN schreven 'm weg, dus `1 WILL` kwam naakt de export in en
          // Emma's testament verloor zijn omschrijving.
          const titel = (ev.title || '').trim();
          const titelZegtIets = !!titel && titel.toLowerCase() !== gedTag.toLowerCase();

          if (titelZegtIets && GEDCOM_ATTRIBUTE_TAGS_WITH_VALUE.has(gedTag)) {
            // Attribuut-tags dragen hun waarde OP de tag-regel (`1 EDUC Latijnse
            // school`). Deze acht types zijn óók kiesbaar in de event-picker, dus
            // ze komen hier langs; `2 TYPE` zou de waarde op een plek zetten waar
            // een lezer 'm niet verwacht — hetzelfde patroon als #635/#636.
            ged += gedLine(1, gedTag, titel);
          } else {
            // #1291 — draagt de gebeurtenis niets anders dan haar tag, dan hoort
            // daar de waarde `Y` bij: "dit gebeurde, meer weten we niet". Zonder
            // die `Y` is `1 PROB` een betekenisloze regel die een lezer mag
            // overslaan — en dan is de nalatenschap stil uit het bestand van de
            // genealoog verdwenen.
            const draagtDetail =
              titelZegtIets ||
              !!(
                ev.date ||
                ev.placeName ||
                ev.address ||
                ev.description ||
                ev.cause ||
                ev.agency ||
                ev.sources?.length ||
                ev.sourceIds?.length ||
                ev.participants?.some(
                  (part: { role?: string }) =>
                    part.role !== 'primary' && part.role !== 'subject' && part.role !== 'partner'
                )
              );
            const magY = !draagtDetail && GEDCOM_EVENT_TAGS_ALLOWING_Y.has(gedTag);
            ged += `1 ${gedTag}${magY ? ' Y' : ''}\n`;
            // Event-tags: `TYPE` is in 5.5.1 toegestaan binnen EVENT_DETAIL onder
            // elke event-tag, dus dáár hoort de omschrijving.
            if (titelZegtIets) ged += `2 TYPE ${titel}\n`;
          }
        }

        // Date — Bug E fix: convert ISO date to GEDCOM format
        if (ev.date) ged += `2 DATE ${toGedcomDate(ev.date)}\n`;

        // Place
        if (ev.placeName) ged += writePlace(2, ev.placeName, ev.placeId);
        if (ev.address) ged += `2 ADDR ${ev.address}\n`;

        // Leeftijd van DEZE persoon op DIT moment, zoals de akte hem noemt.
        // In 5.5.1 hoort AGE in EVENT_DETAIL na PLAC/ADDR en vóór AGNC. Zonder
        // deze regel bleef "oud 27 jaar" uit een bevolkingsregister alleen in
        // onze eigen database staan en verliet het de export nergens.
        const eigenDeelname = ev.participants?.find(
          (part: { personId?: string; age?: unknown }) => part.personId === p.id
        );
        const gedcomLeeftijd = naarGedcomLeeftijd(eigenDeelname?.age);
        if (gedcomLeeftijd)
          ged += `2 AGE ${gedcomLeeftijd}
`;

        // Description/notes
        if (ev.description) ged += gedLine(2, 'NOTE', stripHtml(ev.description));

        // Cause (events table field)
        if (ev.cause) ged += `2 CAUS ${ev.cause}\n`;

        // Agency (events table field)
        if (ev.agency) ged += `2 AGNC ${ev.agency}\n`;

        // Sources — enhanced citations
        if (ev.sources) {
          ged += writeSourCitations(2, ev.sources);
        }

        // Participants as ASSO
        if (ev.participants) {
          for (const part of ev.participants) {
            // Bug B fix: also skip 'subject' role to prevent self-referential ASSO records
            if (part.role === 'primary' || part.role === 'subject') continue;
            const partPersonXref = part.personId ? personIdMap.get(part.personId) : null;
            if (partPersonXref) {
              ged += `2 ASSO ${partPersonXref}\n`;
              ged += `3 RELA ${PARTICIPANT_TO_GEDCOM_ROLE[part.role] || 'OTHER'}\n`;
            } else if (part.personName) {
              ged += gedLine(2, 'NOTE', `${part.role}: ${part.personName}`);
            }
          }
        }
      }

      // OBJE references — link to media with person association (via media_links)
      for (const m of mediaList) {
        const personIds: string[] = m.linkedPersonIds || [];
        if (personIds.includes(p.id) || m.linkedPersonId === p.id) {
          const mediaXref = mediaIdMap.get(m.id);
          if (mediaXref) ged += `1 OBJE ${mediaXref}\n`;
        }
      }

      // FAMS backpointers (families where this person is a spouse)
      // Sort: FAMs with children first, then by FAM index (earlier marriages first)
      const fams = personFamsMap.get(p.id) || [];
      const sortedFams = fams.sort((a: string, b: string) => {
        const famA = famRecords.find((f) => f.xref === a);
        const famB = famRecords.find((f) => f.xref === b);
        const hasChildrenA = famA ? famA.childIds.length : 0;
        const hasChildrenB = famB ? famB.childIds.length : 0;
        if (hasChildrenA !== hasChildrenB) return hasChildrenB - hasChildrenA; // FAMs with children first
        return a.localeCompare(b); // then by xref order (earlier FAMs first)
      });
      for (const fx of sortedFams) ged += `1 FAMS ${fx}\n`;

      // FAMC backpointers (families where this person is a child)
      // #1089: write optional `2 PEDI` sub-tag for adopted/foster relationships.
      // Only GEDCOM-standard values (adopted, foster) are written.
      // biological = GEDCOM default (tag omitted); step/suspected/acknowledged
      // have no GEDCOM 5.5.1 equivalent and are preserved in-app only.
      const famc = personFamcMap.get(p.id) || [];
      for (const fx of famc) {
        ged += `1 FAMC ${fx}\n`;
        const pediTag = childPediMap.get(p.id)?.get(fx);
        if (pediTag) ged += `2 PEDI ${pediTag}\n`;
      }

      // _SOSA custom tag (widely supported by genealogy software)
      const sosa = sosaMap.get(p.id);
      if (sosa) ged += `1 _SOSA ${sosa}\n`;

      // Progress per 100 people
      if (idx % 100 === 0 && idx > 0)
        progress('hsGedExportPeopleCount', 30 + Math.round((30 * idx) / people.length), {
          current: String(idx),
          total: String(people.length),
        });
    });

    // ── Step 3: Write FAM records ──
    progress('hsGedExportFamilies', 60);

    const writeFamEvents = (famEvents: GenealogyEvent[]): string => {
      let out = '';
      for (const event of famEvents) {
        const famTag = gedcomExportTag(event.type, event.gedcomTag) || '';
        if (!famTag) continue;
        if (famTag === 'EVEN') {
          out += `1 EVEN\n`;
          if (event.title) out += `2 TYPE ${event.title}\n`;
        } else {
          out += `1 ${famTag}\n`;
        }
        // Bug E fix: convert ISO date to GEDCOM format
        if (event.date) out += `2 DATE ${toGedcomDate(event.date)}\n`;
        if (event.placeName) out += writePlace(2, event.placeName, event.placeId);
        if (event.address) out += `2 ADDR ${event.address}\n`;
        if (event.description) out += gedLine(2, 'NOTE', stripHtml(event.description));
        if (event.cause) out += `2 CAUS ${event.cause}\n`;
        if (event.agency) out += `2 AGNC ${event.agency}\n`;
        if (event.sources) out += writeSourCitations(2, event.sources);
        if (event.participants) {
          for (const part of event.participants) {
            // Bug B fix: also skip 'subject' role to prevent self-referential ASSO records
            if (part.role === 'primary' || part.role === 'subject') continue;
            const partXref = part.personId ? personIdMap.get(part.personId) : null;
            if (partXref) {
              out += `2 ASSO ${partXref}\n`;
              out += `3 RELA ${PARTICIPANT_TO_GEDCOM_ROLE[part.role] || 'OTHER'}\n`;
            } else if (part.personName) {
              out += gedLine(2, 'NOTE', `${part.role}: ${part.personName}`);
            }
          }
        }
      }
      return out;
    };

    // Spousal FAM records
    for (const fam of famRecords) {
      ged += `0 ${fam.xref} FAM\n1 HUSB ${fam.husbXref}\n1 WIFE ${fam.wifeXref}\n`;

      // Relationship events (from events table)
      const famEventsToExport = relEventsMap.get(fam.rel.id) || [];
      // #1089 fase 2: de union-subtype bepaalt het FAM-niveau marker-tag wanneer er
      // geen expliciet (gedateerd) event is. marriage→MARR Y (default, ongewijzigd),
      // partnership→_UNGD Y, engagement→ENGA Y. Expliciete events worden hieronder
      // gewoon geschreven; we voegen alleen de Y-marker toe als dat tag-event ontbreekt.
      const subtypeTag = partnerSubtypeToGedcomTag(fam.rel.relationSubtype);
      const hasSubtypeEvent = famEventsToExport.some(
        (e: any) =>
          e.gedcomTag === subtypeTag ||
          EVENT_TO_GEDCOM_TAG[e.type as keyof typeof EVENT_TO_GEDCOM_TAG] === subtypeTag
      );
      if (!hasSubtypeEvent) ged += `1 ${subtypeTag} Y\n`;
      ged += writeFamEvents(famEventsToExport);

      // Relationship notes & sources
      if (fam.rel.notes) ged += gedLine(1, 'NOTE', stripHtml(fam.rel.notes));
      ged += writeSourRefs(1, fam.rel.sourceIds);

      // Children
      for (const childXref of fam.children) ged += `1 CHIL ${childXref}\n`;
    }

    // FAM records for parents without a registered partner-relationship (one or two)
    const xrefOf = (id: string) => personIdMap.get(id) || `@I${id}@`;
    const genderOf = (id: string) => people.find((p: any) => p.id === id)?.gender;
    // #1389 B11 — één partner, geen relatie: FAM met alleen HUSB óf WIFE
    for (const lf of loneFams) {
      ged += `0 ${lf.xref} FAM\n`;
      ged += `1 ${genderOf(lf.personId) === 'F' ? 'WIFE' : 'HUSB'} ${xrefOf(lf.personId)}\n`;
      ged += writeFamEvents(lf.events);
    }

    for (const spf of parentFams) {
      // GEDCOM offers only HUSB/WIFE: non-female parents fill HUSB first, so a
      // lone mother still writes WIFE and no parent is ever dropped.
      const females = spf.parentIds.filter((id) => genderOf(id) === 'F');
      const others = spf.parentIds.filter((id) => genderOf(id) !== 'F');
      const husbId = others[0] ?? (females.length > 1 ? females[0] : undefined);
      const wifeId = females.find((id) => id !== husbId) ?? others[1];
      ged += `0 ${spf.xref} FAM\n`;
      if (husbId) ged += `1 HUSB ${xrefOf(husbId)}\n`;
      if (wifeId) ged += `1 WIFE ${xrefOf(wifeId)}\n`;
      for (const childId of spf.childIds) ged += `1 CHIL ${xrefOf(childId)}\n`;
    }

    // ── Step 4: Generate SOUR records ──
    progress('hsGedExportSources', 80);
    for (const s of sources) {
      const xref = sourceIdMap.get(s.id);
      if (!xref) continue;
      ged += `0 ${xref} SOUR\n`;
      // #1291 — het aktetype ging verloren: een memorie van successie kwam bij de
      // ontvanger binnen als naamloos bronnetje. `1 DATA` / `2 EVEN` is in 5.5.1
      // de plek voor "welk soort gebeurtenis legt deze bron vast". Types zonder
      // eigen event-tag (boek, krant, notarieel) slaan dit blok over — zie
      // SOURCE_TYPE_TO_GEDCOM_EVENT.
      const bronEvent = SOURCE_TYPE_TO_GEDCOM_EVENT[s.type as string];
      if (bronEvent) ged += `1 DATA\n2 EVEN ${bronEvent}\n`;
      if (s.title) ged += gedLine(1, 'TITL', stripHtml(s.title));
      if (s.author) ged += gedLine(1, 'AUTH', stripHtml(s.author));
      // C-02 haalde de URL uit `PUBL` weg zodat de citatie daar kon blijven staan
      // — terecht, want `PUBL` is de publicatiegegevens. Maar de URL belandde op
      // `_AURL`, en dat tag is van onszelf: geen enkele lezer kent 'm, onze eigen
      // import evenmin. Eén klantexport had 57 `_AURL`-regels en geen enkele URL
      // die haar programma kon openen. Geen van beide GEDCOM-versies heeft een
      // URL-tag op SOUR, dus hoort hij onder `PUBL` — als `CONT` onder de citatie
      // wanneer die er ook is. `_AURL` blijft ernaast staan voor de
      // aggregator-roundtrip (#856).
      //
      // LET OP — hier stond `is70 ? WWW : _AURL`, met de aanname "7.0 heeft
      // `WWW`". Die aanname is onjuist en produceerde ongeldige 7.0-bestanden.
      // `WWW` bestaat al sinds 5.5.1, maar in beide versies alleen binnen een
      // adresstructuur (archief, indiener), niet op een bron. De toegestane
      // substructuren van record-SOUR in 7.0 zijn: ABBR, AUTH, CHAN, CREA, DATA,
      // EXID, NOTE, OBJE, PUBL, REFN, REPO, SNOTE, TEXT, TITL, UID.
      // Bron: https://gedcom.io/terms/v7/record-SOUR
      //
      // ponytail: `PUBL` is een compromis, geen ideaal. In 7.0 is de zuivere plek
      // voor een scan-verwijzing een multimedia-object (`OBJE` → `FILE <url>`).
      // Dat vraagt een eigen record per bron-URL; doen zodra er een reden is om
      // die records sowieso aan te maken. Geldig is het nu wel, en dat was het
      // hiervoor niet.
      const publRegels = [s.citation ? stripHtml(s.citation) : '', s.url || ''];
      const publ = publRegels.filter(Boolean).join('\n');
      if (publ) ged += gedLine(1, 'PUBL', publ);
      if (s.url) ged += gedLine(1, '_AURL', s.url);
      if (s.description) ged += gedLine(1, 'NOTE', stripHtml(s.description));
      // REPO koppeling (Refs #668): link source aan repository + optional CALN.
      // Aggregator (Open Archieven / WieWasWie) gaat NIET via REPO maar via
      // _APID/_AURL/_ACDA/_TLAY tags in buildSourceLines hieronder (#856).
      if (s.repositoryId) {
        const repoXref = repoIdMap.get(s.repositoryId);
        if (repoXref) {
          ged += `1 REPO ${repoXref}\n`;
          if (s.callNumber) ged += `2 CALN ${s.callNumber}\n`;
        }
      }
      // Citation-data uitbreiding (#856 — archieven-redesign Fase 6):
      // aggregator-tags + BR-folio + mutaties + proof-argument NOTE.
      const citationLog = citationBySourceId.get(s.id);
      if (citationLog) {
        const citationLines = buildSourceLines(citationLog);
        for (const line of citationLines) {
          ged += `${line}\n`;
        }
      }
    }

    // ── Step 4b: Generate REPO records (Refs #668) ──
    progress('hsGedExportRepositories', 85);
    for (const r of repositories) {
      const xref = repoIdMap.get(r.id);
      if (!xref) continue;
      ged += `0 ${xref} REPO\n`;
      if (r.name) ged += gedLine(1, 'NAME', stripHtml(r.name));
      if (r.address) ged += gedLine(1, 'ADDR', stripHtml(r.address));
      if (r.phone) ged += `1 PHON ${r.phone}\n`;
      if (r.email) ged += `1 EMAIL ${r.email}\n`;
      if (r.website) ged += `1 WWW ${r.website}\n`;
      if (r.notes) ged += gedLine(1, 'NOTE', stripHtml(r.notes));
    }

    // ── Step 5: Generate OBJE records ──
    progress('hsGedExportMedia', 90);
    for (const m of mediaList) {
      const xref = mediaIdMap.get(m.id);
      if (!xref) continue;
      ged += `0 ${xref} OBJE\n`;
      // #883: FILE moet de file-path/URL zijn (niet de filename), zodat de
      // ontvangende GEDCOM-reader het bestand of de scan kan bereiken.
      // Pre-fix gebruikte m.filename (vaak undefined want media-mapping
      // was incomplete), resulterend in lege OBJE-records. Nu m.filePath
      // (echte URL/path) als FILE; m.filename als TITL fallback wanneer
      // user geen caption heeft ingevuld.
      const fileRef = m.filePath || m.filename || '';
      if (fileRef) {
        ged += `1 FILE ${fileRef}\n`;
        if (is70 && m.mimeType) {
          // GEDCOM 7.0: FORM as full MIME type
          ged += `2 FORM ${m.mimeType}\n`;
        } else {
          // 5.5.1: FORM as extension
          const ext = m.mimeType?.split('/')?.[1] || (m.filename || '').split('.').pop() || '';
          if (ext) ged += `2 FORM ${ext}\n`;
        }
      }
      const titleText = m.caption || m.filename || '';
      if (titleText) ged += gedLine(1, 'TITL', titleText);
    }

    // ── Step 6: Generate NOTE/SNOTE records ──
    progress('hsGedExportNotes', 93);
    const noteTag = is70 ? 'SNOTE' : 'NOTE';
    for (const note of projectNotes) {
      const xref = noteIdMap.get(note.id);
      if (!xref || !note.content?.trim()) continue;
      if (is70 && note.isHtml) {
        // GEDCOM 7.0: SNOTE with MIME text/html
        ged += `0 ${xref} ${noteTag}\n`;
        ged += `1 MIME text/html\n`;
        ged += gedLine(1, 'CONT', note.content);
      } else {
        // 5.5.1 or plain text: NOTE with CONT/CONC lines via gedLine
        const plainText = stripHtml(note.content);
        if (plainText) ged += gedLine(0, `${xref} ${noteTag}`, plainText);
      }
    }

    // ── Trailer ──
    progress('hsGedExportFinishing', 95);
    ged += `0 TRLR\n`;
    progress('hsGedExportDone', 100);

    // Convert LF to CRLF for GEDCOM 5.5.1 compliance
    if (!is70) ged = ged.replace(/\n/g, '\r\n');

    return new Blob([ged], { type: 'text/plain;charset=utf-8' });
  },
};
